import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import ProfileForm from '@/components/profile/ProfileForm';
import NotificationSettings from '@/components/profile/NotificationSettings';
import PaymentReceivingForm from '@/components/profile/PaymentReceivingForm';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowUpCircle, Users, Settings, CreditCard, CheckCircle2, Calendar, Shield, RefreshCw, ChevronDown } from 'lucide-react';
import { useBusinessPlans } from '@/hooks/useBusinessPlans';
import PlanUpgradeDialog from '@/components/superadmin/plan-upgrade/PlanUpgradeDialog';
import { processSubscription } from '@/utils/subscriptionService';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog } from '@/components/ui/dialog';

const AdminProfile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading, activeTab, setActiveTab, user, handleLogout } = useAdminSettings();
  const { 
    subscriptionStatus, 
    trialDaysLeft: daysRemaining,
    isLoading: isSubscriptionLoading 
  } = useSubscription();
  
  const { businessPlans, isLoading: isLoadingPlans } = useBusinessPlans();
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const isTrialUser = subscriptionStatus === 'trial' || subscriptionStatus === 'expired';

  useEffect(() => {
    if (businessPlans.length > 0 && user) {
      const userPlanId = user.planId || 'starter';
      const userPlan = businessPlans.find(plan => plan.id === userPlanId) || businessPlans[0];
      setCurrentPlan(userPlan);
    }
  }, [businessPlans, user]);
  
  const handleViewPlans = () => {
    navigate('/admin/plans', { 
      state: { 
        fromSubscription: true,
        isTrialUser
      }
    });
  };
  
  const handleUpgradeClick = () => {
    setUpgradeDialogOpen(true);
  };
  
  const handlePlanSelection = (planId: string) => {
    const plan = businessPlans.find(p => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
    }
  };
  
  const getAvailableUpgradePlans = () => {
    if (!currentPlan) return [];
    
    return businessPlans.filter(plan => 
      plan.monthlyPrice > currentPlan.monthlyPrice
    );
  };
  
  const handleProcessPayment = async () => {
    if (!selectedPlan) return;
    
    setIsProcessing(true);
    try {
      const result = await processSubscription(selectedPlan.id);
      
      if (result.success) {
        setUpgradeDialogOpen(false);
        setCurrentPlan(selectedPlan);
        toast({
          title: "Plan Upgraded Successfully",
          description: "Your subscription has been updated to the new plan."
        });
      } else {
        toast({
          title: "Payment Failed",
          description: result.message || "There was a problem processing your payment.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getNextBillingDate = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    
    return nextMonth.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  return (
    <AdminLayout
      isLoading={isLoading || isSubscriptionLoading || isLoadingPlans}
      onLogout={handleLogout}
      userRole={user?.role}
    >
      <div className="container max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-primary-blue">My Profile</h1>
        
        {isTrialUser && daysRemaining !== undefined && (
          <Card className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-md overflow-hidden">
            <CardHeader className="pb-2 bg-blue-500 text-white">
              <CardTitle>Trial Status</CardTitle>
              <CardDescription className="text-blue-100">
                Your trial {daysRemaining > 0 ? 'will expire' : 'has expired'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {daysRemaining > 0 ? (
                <div className="flex items-center mb-4">
                  <Calendar className="h-6 w-6 text-blue-600 mr-3" />
                  <p className="text-blue-800">
                    You have <span className="font-bold">{daysRemaining} days</span> remaining in your trial period.
                  </p>
                </div>
              ) : (
                <div className="flex items-center mb-4">
                  <Shield className="h-6 w-6 text-red-500 mr-3" />
                  <p className="text-red-600">
                    Your trial has expired. Please upgrade to continue using all features.
                  </p>
                </div>
              )}
              
              <Button 
                variant="primary"
                onClick={handleViewPlans}
                className="mt-2"
              >
                View All Plans
              </Button>
            </CardContent>
          </Card>
        )}
        
        <Card className="mb-8 bg-gradient-to-br from-white to-green-50 border-green-200 shadow-md overflow-hidden">
          <CardHeader className="pb-2 border-b border-green-100">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-xl text-green-800">Current Business Plan</CardTitle>
                <CardDescription>
                  Your subscription details and resource limits
                </CardDescription>
              </div>
              {currentPlan && (
                <Badge className="bg-green-500 text-white px-3 py-1">
                  {currentPlan.name}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {currentPlan ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100 transform transition-transform hover:scale-105 hover:shadow-md">
                  <div className="flex items-center text-green-600 mb-2">
                    <Users className="h-5 w-5 mr-2" />
                    <h3 className="font-medium">Total Users</h3>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{currentPlan.maxReferrals}</p>
                  <p className="text-sm text-gray-500">Maximum end users</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 transform transition-transform hover:scale-105 hover:shadow-md">
                  <div className="flex items-center text-blue-600 mb-2">
                    <Settings className="h-5 w-5 mr-2" />
                    <h3 className="font-medium">Managers</h3>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{currentPlan.maxManagers}</p>
                  <p className="text-sm text-gray-500">Maximum manager accounts</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border border-purple-100 transform transition-transform hover:scale-105 hover:shadow-md">
                  <div className="flex items-center text-purple-600 mb-2">
                    <CreditCard className="h-5 w-5 mr-2" />
                    <h3 className="font-medium">Referrals</h3>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">{currentPlan.maxReferralLinks}</p>
                  <p className="text-sm text-gray-500">Maximum referral campaigns</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500">No plan information available</p>
              </div>
            )}
            
            <div className="space-y-3 mt-4">
              <h3 className="font-medium text-green-800 border-b border-green-100 pb-2">Plan Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-2">
                {currentPlan?.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start py-1">
                    {feature.included ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <div className="h-5 w-5 border border-gray-300 rounded-full mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={feature.included ? 'text-gray-800' : 'text-gray-400'}>
                      {feature.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between items-center pt-4 border-t border-green-100 bg-green-50/50">
            <div className="text-sm text-gray-600 flex items-center">
              <RefreshCw className="h-4 w-4 mr-2 text-green-600" />
              Next billing: {getNextBillingDate()}
            </div>
            <div className="space-x-3">
              <Button 
                variant="outline" 
                onClick={handleViewPlans}
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                View All Plans
              </Button>
              <Button 
                onClick={handleUpgradeClick}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              >
                <ArrowUpCircle className="mr-2 h-4 w-4" />
                Upgrade Plan
              </Button>
            </div>
          </CardFooter>
        </Card>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white rounded-lg p-4 shadow-sm">
          <TabsList className="mb-8 bg-gray-100 p-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">Profile</TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">Notifications</TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">Payment Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="profile">
            <ProfileForm 
              user={user} 
              onProfileUpdate={async (data) => {
                toast({
                  title: "Profile Updated",
                  description: "Your profile information has been updated successfully.",
                });
                return Promise.resolve();
              }} 
            />
          </TabsContent>
          
          <TabsContent value="notifications">
            <NotificationSettings userId={user?.id || ''} />
          </TabsContent>
          
          <TabsContent value="payment">
            <PaymentReceivingForm 
              onSave={async (data) => {
                console.log('Payment details to save:', data);
                return Promise.resolve();
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {currentPlan && (
        <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
          <PlanUpgradeDialog
            open={upgradeDialogOpen}
            onOpenChange={setUpgradeDialogOpen}
            currentPlan={currentPlan}
            targetPlan={selectedPlan || (getAvailableUpgradePlans()[0] || null)}
            onProcessPayment={handleProcessPayment}
            isProcessing={isProcessing}
          >
            <div className="mb-6">
              <label htmlFor="plan-select" className="block text-sm font-medium mb-2">
                Select Plan to Upgrade
              </label>
              <Select 
                onValueChange={handlePlanSelection}
                defaultValue={getAvailableUpgradePlans()[0]?.id}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableUpgradePlans().map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.monthlyPrice}/month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </PlanUpgradeDialog>
        </Dialog>
      )}
    </AdminLayout>
  );
};

export default AdminProfile;
